import React, { useRef, useState } from "react";
import { toast } from "react-toastify";
import Select from "react-select";
import { Button } from "aible";
import AccountSelector from "containers/Accounts/AccountSelector";
import LoadingIndicator from "components/LoadingIndicator/LoadingIndicator";
import styles from "./style.module.css";
import { LineInput } from "components/Inputs/LineInput";
import { isEnabled } from "utils/featureFlags";
import { handleFileChange, validateFileExtension, getUploadEncoding, getUploadEncodingDefault, getUploadDelimiter } from "containers/Upload/reducer";
import { useDispatch, useSelector } from "types/store";
import { getSelectedAccount, getSelectedAccountId } from "containers/Accounts/reducer";
import { getCurrentNextId, updateDatasetId } from "containers/Next/NextReducer";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/aible/Dialog/Dialog";
import { customMapper, selectOnChangeWrapper } from "@/utils/select";
import { keysOf } from "@/utils/collect";
const DEFAULT_SHELL_STATE = { url: "", name: "", description: "" };
const fileChangeHandler = handleFileChange;
export const HiddenFilePicker = ({ innerRef, onStart, onComplete, selectDatasetOnComplete, readOptions }) => {
    const dispatch = useDispatch();
    const nextId = useSelector((state) => getCurrentNextId(state)) ?? "new";
    const handleChange = async (event) => {
        if (event.currentTarget === null) {
            return Promise.resolve(undefined);
        }
        if (!("files" in event.currentTarget)) {
            return Promise.resolve(undefined);
        }
        const file = event.currentTarget?.files ? event.currentTarget.files[0] : undefined;
        if (file !== undefined && validateFileExtension(file)) {
            const dataset = await dispatch(fileChangeHandler(file, event.currentTarget, {}, readOptions));
            if (dataset && selectDatasetOnComplete) {
                dispatch(updateDatasetId(nextId, dataset?.id));
            }
            return await Promise.resolve(dataset);
        }
        else {
            return Promise.resolve(undefined);
        }
    };
    return (React.createElement("div", { style: { display: "none" } },
        React.createElement("input", { type: "file", id: "fUpload", ref: innerRef, onChange: async (event) => {
                if (onStart !== undefined) {
                    onStart();
                }
                const result = await handleChange(event);
                if (onComplete) {
                    onComplete(result);
                }
                return result;
            } })));
};
const OPTION_DEFAULT_VALUES = {
    type: [undefined, "CSV", "JSONL", "PARQUET", "ARROW"],
    compute_environment: [undefined, "memory", "data_warehouse"],
    compute_engine: [undefined, "cpu", "gpu"],
    compression: [undefined, "GZIP", "LZ4"],
    delimiter: [undefined, ",", ";", "|", "\t"],
    date_formats: [undefined, ["%Y-%m-%d %H:%M:%S %z"], ["%m/%d/%y"]],
    encoding: ["UTF-8", "UTF-16", "UTF-32", "windows-1252", "ASCII", undefined],
    null_values: [undefined],
    newlines_in_values: [true, false]
};
const OPTION_MAPPING_FUNCTION = {
    type: (type) => {
        if (type === undefined) {
            return "Auto";
        }
        switch (type) {
            case "CSV":
                return "CSV";
            case "PARQUET":
                return "Parquet";
            case "ARROW":
                return "Arrow IPC";
            case "JSONL":
                return "JSON Lines";
        }
    },
    compute_environment: (environment) => {
        if (environment === undefined) {
            return "Auto";
        }
        switch (environment) {
            case "data_warehouse":
                return "Cloud Data Warehouse";
            case "memory":
            default:
                return "Local Instance";
        }
    },
    compute_engine: (engine) => {
        if (engine === undefined) {
            return "Auto";
        }
        switch (engine) {
            case "gpu":
                return "GPU (Hardware and Drivers Required)";
            case "cpu":
            default:
                return "CPU";
        }
    },
    compression: (compression) => {
        if (compression === undefined) {
            return "Auto";
        }
        switch (compression) {
            case "GZIP":
                return "GZIP";
            case "BROTLI":
                return "Brotli";
            case "NONE":
                return "None";
            default:
                return compression;
        }
    },
    encoding: (encoding) => {
        if (encoding === undefined) {
            return "Auto Detect";
        }
        switch (encoding) {
            case "Windows-1252":
                return "windows-1252";
            default:
                return encoding;
        }
    },
    date_formats: (formats) => {
        if (formats === undefined) {
            return "ISO-8601";
        }
        return formats.join(",");
    },
    null_values: (values) => {
        if (values === undefined) {
            return "";
        }
        return values.join(",");
    },
    delimiter: (delimiter) => {
        if (delimiter === undefined) {
            return "Auto Detect";
        }
        else if (delimiter === "\t") {
            return "Tab";
        }
        return delimiter;
    },
    newlines_in_values: (newlines_in_values) => {
        return newlines_in_values ? "True" : "False";
    }
};
const OPTION_MAPPERS = {
    type: customMapper(OPTION_MAPPING_FUNCTION["type"]),
    compute_environment: customMapper(OPTION_MAPPING_FUNCTION["compute_environment"]),
    compute_engine: customMapper(OPTION_MAPPING_FUNCTION["compute_engine"]),
    compression: customMapper(OPTION_MAPPING_FUNCTION["compression"]),
    encoding: customMapper(OPTION_MAPPING_FUNCTION["encoding"]),
    date_formats: customMapper(OPTION_MAPPING_FUNCTION["date_formats"]),
    null_values: customMapper(OPTION_MAPPING_FUNCTION["null_values"]),
    delimiter: customMapper(OPTION_MAPPING_FUNCTION["delimiter"]),
    newlines_in_values: customMapper(OPTION_MAPPING_FUNCTION["newlines_in_values"])
};
const getOptionMapper = (key) => {
    return OPTION_MAPPERS[key];
};
const getDefaultOptions = (key) => {
    return OPTION_DEFAULT_VALUES[key];
};
const getDefaultValue = (key) => {
    return getDefaultOptions(key)[0];
};
const getPlaceholderValue = (key) => {
    return OPTION_MAPPING_FUNCTION[key](getDefaultValue(key));
};
const DataSourceReadOptionInput = ({ valueKey, label, value, updateValue }) => {
    const mapper = getOptionMapper(valueKey);
    const options = getDefaultOptions(valueKey);
    return (React.createElement("div", { className: styles.uploadModalOption },
        React.createElement("div", { className: styles.uploadModalInputLabel }, label),
        React.createElement("div", { className: styles.uploadModalInput },
            React.createElement(Select, { options: mapper.mapAll(options), value: mapper.map(value), onChange: selectOnChangeWrapper((value) => updateValue(value ?? getDefaultValue(valueKey))), menuPlacement: "bottom", placeholder: getPlaceholderValue(valueKey), isClearable: value != options[0] }))));
};
const UploadModalAdvancedOptions = ({ readOptions, updateReadOptions }) => {
    const [showAdvancedOptions, updateShowAdvancedOptions] = useState(false);
    const encoding = useSelector((state) => getUploadEncoding(state));
    const defaultEncoding = useSelector((state) => getUploadEncodingDefault(state));
    const delimiter = useSelector((state) => getUploadDelimiter(state));
    const account = useSelector((state) => getSelectedAccount(state));
    const retShowAdvancedOptions = encoding !== defaultEncoding || delimiter !== undefined || showAdvancedOptions;
    const handleUpdateReadOptions = (rawUpdate) => {
        const update = { ...readOptions, ...rawUpdate };
        updateReadOptions(keysOf(update)
            .filter((k) => update[k] != undefined)
            .reduce((o, k) => {
            o[k] = update[k];
            return o;
        }, {}));
    };
    return (React.createElement("div", { className: styles.advancedOptionsWrapper },
        React.createElement("a", { className: styles.uploadModalLink, onClick: () => {
                updateShowAdvancedOptions(!showAdvancedOptions);
            } }, "Advanced Options"),
        retShowAdvancedOptions && (React.createElement(DataSourceReadOptionInput, { valueKey: "type", label: "File Type", value: readOptions["type"], updateValue: (type) => handleUpdateReadOptions({ type }) })),
        retShowAdvancedOptions && account.type == "GCP" && (React.createElement(React.Fragment, null,
            React.createElement(DataSourceReadOptionInput, { valueKey: "compute_environment", label: "Compute Environment", value: readOptions["compute_environment"], updateValue: (compute_environment) => handleUpdateReadOptions({ compute_environment }) }))),
        retShowAdvancedOptions && (React.createElement(React.Fragment, null,
            React.createElement(DataSourceReadOptionInput, { valueKey: "compute_engine", label: "Compute Engine", value: readOptions["compute_engine"], updateValue: (compute_engine) => handleUpdateReadOptions({ compute_engine }) }))),
        retShowAdvancedOptions && readOptions.type === "JSONL" && (React.createElement(React.Fragment, null,
            React.createElement(DataSourceReadOptionInput, { valueKey: "newlines_in_values", label: "Allow Newlines in Values", value: readOptions["newlines_in_values"], updateValue: (newlines_in_values) => handleUpdateReadOptions({ newlines_in_values }) }))),
        " ",
        retShowAdvancedOptions && readOptions.type === "CSV" && (React.createElement(React.Fragment, null,
            React.createElement(DataSourceReadOptionInput, { valueKey: "encoding", label: "Encoding", value: readOptions["encoding"], updateValue: (encoding) => handleUpdateReadOptions({ encoding }) }),
            React.createElement(DataSourceReadOptionInput, { valueKey: "delimiter", label: "Delimiter", value: readOptions["delimiter"], updateValue: (delimiter) => handleUpdateReadOptions({ delimiter }) }),
            React.createElement(DataSourceReadOptionInput, { valueKey: "date_formats", label: "Date Format", value: readOptions["date_formats"], updateValue: (date_formats) => handleUpdateReadOptions({ date_formats }) }),
            React.createElement(DataSourceReadOptionInput, { valueKey: "newlines_in_values", label: "Allow Newlines in Values", value: readOptions["newlines_in_values"], updateValue: (newlines_in_values) => handleUpdateReadOptions({ newlines_in_values }) })))));
};
const UploadModal = ({ show, handleClose, onUploadCompleted }) => {
    const dispatch = useDispatch();
    const pickerRef = useRef(null);
    const selectedAccountId = useSelector((state) => getSelectedAccountId(state));
    const [shellState, updateShellState] = useState(DEFAULT_SHELL_STATE);
    const [isUploading, updateIsUploading] = useState(false);
    const [readOptions, updateReadOptions] = useState({
        encoding: getDefaultValue("encoding")
    });
    const handleUpdateShellState = (update) => {
        updateShellState({ ...shellState, ...update });
    };
    const handleReset = () => {
        updateShellState(DEFAULT_SHELL_STATE);
    };
    const createShellDataset = () => {
        // create dummy file - not actually uploaded
        const blob = new Blob(["1"], { type: "text/plain" });
        const file = new File([blob], shellState.name, { type: blob.type });
        handleUpdateShellState({ url: "Creating..." });
        return dispatch(fileChangeHandler(file, undefined, {
            skipUpload: true,
            description: shellState.description
        })).then((dataset) => {
            const splitUrl = dataset.source.url.split("/");
            const uploadId = splitUrl[splitUrl.length - 2];
            handleUpdateShellState({
                url: `${window.location.origin}/upload/${uploadId}`
            });
            return Promise.resolve(dataset);
        });
    };
    return (React.createElement(Dialog, { open: show, onOpenChange: () => handleClose() },
        React.createElement(DialogContent, { size: "sm" },
            React.createElement(DialogHeader, null,
                React.createElement(DialogTitle, null, "Upload Data")),
            React.createElement(HiddenFilePicker, { innerRef: pickerRef, onStart: () => updateIsUploading(true), onComplete: (dataset) => {
                    if (onUploadCompleted) {
                        onUploadCompleted(dataset);
                    }
                    updateIsUploading(false);
                    handleClose();
                }, readOptions: readOptions }),
            React.createElement("div", { className: styles.uploadModalBody, style: { flexFlow: "column" } },
                React.createElement("div", { className: styles.uploadModalGroupSmall },
                    React.createElement("div", { className: styles.uploadModalText }, "Select Account"),
                    React.createElement("div", { className: styles.uploadModalSelector },
                        React.createElement(AccountSelector, null)),
                    React.createElement(UploadModalAdvancedOptions, { key: String(show), readOptions: readOptions, updateReadOptions: updateReadOptions }),
                    React.createElement("div", { className: styles.uploadModalSelector },
                        React.createElement("div", { style: { display: "flex" } },
                            React.createElement(Button, { type: "primary", style: { width: "170px" }, disabled: !selectedAccountId || shellState.url !== "" || isUploading, onClick: () => pickerRef?.current?.click() }, isUploading ? "Uploading..." : "Select Data to Upload"),
                            isUploading && (React.createElement(LoadingIndicator, { style: {
                                    margin: "0px",
                                    marginTop: "-8px",
                                    marginLeft: "10px",
                                    marginRight: "10px",
                                    transform: "scale(0.6)"
                                } }))))),
                (!isEnabled("chat-aible-consumer") || isEnabled("aible-optimize")) && isEnabled("dataset-upload") && (React.createElement("div", { className: styles.uploadModalGroupSmall },
                    React.createElement("div", { className: styles.uploadModalText },
                        React.createElement("strong", null, "Alternative: "),
                        "Create Place Holder Dataset"),
                    React.createElement("div", { style: { wordWrap: "break-word", height: "25px" } }, "Don't have access to the data or need someone else to upload?"),
                    React.createElement("div", { style: { wordWrap: "break-word" } }, shellState.url !== "" && shellState.url !== "Creating..." ? (React.createElement("div", null,
                        React.createElement(LineInput, { inputStyle: { width: "100%" }, value: shellState.url, isMediumBox: true }),
                        React.createElement("div", { className: styles.uploadModalButtons },
                            React.createElement(Button, { disabled: shellState.url === "Creating...", onClick: () => {
                                    navigator.clipboard.writeText(shellState.url);
                                    toast("Url copied to clipboard", {
                                        type: toast.TYPE.SUCCESS,
                                        position: toast.POSITION.TOP_CENTER,
                                        autoClose: 3500
                                    });
                                } }, "Copy Url to Clipboard"),
                            React.createElement(Button, { style: { marginLeft: "auto" }, disabled: shellState.url === "Creating...", onClick: () => {
                                    handleReset();
                                } }, "Create another place holder dataset")))) : (React.createElement("div", null,
                        React.createElement(LineInput, { isDisabled: shellState.url === "Creating...", value: shellState.name, placeholder: "Specify a name for this dataset", onChange: (v) => handleUpdateShellState({ name: v.substring(0, 64) }), isMediumBox: true, updateInstant: true }),
                        React.createElement(LineInput, { isDisabled: shellState.url === "Creating...", value: shellState.description, placeholder: "Add a short description (Optional)", onChange: (v) => handleUpdateShellState({
                                description: v.substring(0, 256)
                            }), isMediumBox: true, updateInstant: true }),
                        React.createElement(Button, { type: "primary", disabled: !selectedAccountId || shellState.name === undefined || shellState.name.length === 0, onClick: () => createShellDataset(), isLoading: shellState.url === "Creating..." }, "Generate Place Holder and Create Upload Url"))))))),
            React.createElement(DialogFooter, null,
                React.createElement(Button, { type: "secondary", onClick: () => handleClose() }, "Close")))));
};
export default UploadModal;
